import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { pick } from "@/lib/wsCopy";
import {
  INTERVIEW_COPY, pickSourceAware, INTERVIEW_QUESTIONS,
} from "@/lib/interviewCopy";
import { Assistant, UserReply, QuickReplies } from "./ChatShell";
import { GenWaitStrip } from "./GenLoaders";
import { usePrefersReducedMotion, useIsMobile } from "./shared";

/**
 * The onboarding interview — three questions asked WHILE the site is being read.
 *
 * WHERE THE TRUTH LIVES. Nothing about "which question are we on" is stored in
 * this component, in sessionStorage, or in a counter. It is derived every render
 * from the server's `interview` object on the generation status poll:
 *
 *   interview.questions        the ask order
 *   interview.answers[qid]     {text, skipped, at} — present == dealt with
 *   interview.next_question_id THE cursor, computed server-side
 *   interview.state            "pending" | "resolved"
 *
 * That is what makes abandonment resumable for free. A tab closed mid-Q2 and
 * reopened an hour later re-attaches to the same job (the existing
 * GEN_SESSION_KEY / GET /brands/generate/active path — no new mechanism), reads
 * `next_question_id`, and lands on Q2 with Q1's answer already back in the
 * transcript. A client-side cursor would have to guess, and would guess wrong
 * exactly when it matters: after the tab that held it is gone.
 *
 * WHAT IS RENDERED UNDERNEATH. Progress never leaves the screen while the
 * questions are up (Ori's flag 3 — if the questions occupy the space progress
 * held, this feature has replaced honest progress with a distraction). While the
 * crawl is still running that strip really moves. The moment Phase 1 lands, the
 * server sets `waiting_on_owner` and the strip freezes — see GenWaitStrip.
 *
 * MANDATORY, AND WHY (owner, 20 Jul 2026). Q1/Q2 have no skip and no skip
 * affordance. Measured on the real brand, same site: answering yields 13 domain
 * terms to search with, skipping yields 4 bare initialisms. The escape hatch is
 * not removed for conversion — it is removed because there is nothing behind it.
 * Q3 keeps ONE secondary button, and it is an answer ("Nothing to add"), not an
 * exit; it is rendered only where the catalog defines `noAnswer`, so a question
 * id this build does not recognise gets no button at all. There is deliberately
 * no fallback label: a generic "Skip" is exactly how skip would come back.
 *
 * THE PROBE. A one-word answer gets ONE gentle re-ask (`probe`) shown alongside
 * the release valve (`probeAccept`) — both on screen at once, so the way through
 * is visible at the same instant as the ask. The next Send goes through
 * unconditionally, whatever they typed. Never twice for the same question.
 * Neither probe line is an error state: a brief answer is a person being brief.
 */
export default function InterviewStep({ gen, onSubmit, showProgress }) {
  const mobile = useIsMobile();
  const reduced = usePrefersReducedMotion();
  const interview = gen?.interview;

  // The question catalog. Fetched, not hardcoded: the same wording is keyed into
  // the ICP/keyword prompts server-side, and a second copy here is how the
  // screen and the prompt drift apart.
  const [catalog, setCatalog] = useState(null);
  const [maxChars, setMaxChars] = useState(600);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/brands/generate/interview/questions");
        if (!alive) return;
        const byId = {};
        (r.data?.items || []).forEach((q) => { byId[q.id] = q.prompt; });
        setCatalog(byId);
        if (r.data?.max_answer_chars) setMaxChars(r.data.max_answer_chars);
      } catch (_) {
        // Leave `catalog` null. We render the transcript and the progress strip
        // but ask NOTHING — a question box with no question is worse than no
        // question box, and the job stays resumable either way.
        if (alive) setCatalog({});
      }
    })();
    return () => { alive = false; };
  }, []);

  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const taRef = useRef(null);

  // The probe pair. `probed` is the once-per-question latch: once we have
  // re-asked, the next Send is unconditional, so nobody can be held at a
  // question by typing tersely twice. `notice` is which line is on screen —
  // "probe" (thin answer) or "empty" (nothing typed). Both reset on `nextId`.
  const [probed, setProbed] = useState(false);
  const [notice, setNotice] = useState(null);

  const order = interview?.questions || [];
  const answers = interview?.answers || {};
  const resolved = interview?.state === "resolved";
  const nextId = resolved ? null : interview?.next_question_id;

  // Which closing line is true. Decided ONCE, when the interview resolves, from
  // the real phase — never from how much the owner typed (Ori: if the closing
  // copy branches on effort, they feel the branch).
  //
  // `doneEarly` says "I'm still reading through your site", which is true only
  // while Phase 1 is genuinely still crawling. Once the profile has landed we
  // are past reading the site, so the other line is the honest one.
  // Latched, and one of the three values matters as much as the other two:
  // "none". Both strings talk about work that is STILL HAPPENING, so neither is
  // true of a job that has already finished. That is not hypothetical — it is
  // exactly what a cold resume hits: reopen onboarding on a completed job and
  // the transcript is rebuilt from scratch, so this line would be freshly
  // rendered, as if current, saying "Still working on your brand" over a
  // finished brand sitting right underneath it. So it is emitted only if we are
  // here while the job is genuinely still running, and once emitted it is kept
  // (it is chat history by then, and history should not evaporate mid-session).
  const [closing, setClosing] = useState(null);
  useEffect(() => {
    if (!resolved || closing !== null) return;
    if (gen?.status !== "running") { setClosing("none"); return; }
    setClosing(gen?.phase === "crawling" ? "early" : "done");
  }, [resolved, closing, gen?.phase, gen?.status]);

  // New question → clear the box, drop both probe flags, and (desktop only) take
  // focus. Still not on mobile: autofocus there raises the on-screen keyboard,
  // which eats half the viewport and pushes the question — the thing they have
  // to read before they can answer it — up under the fold. They tap the box when
  // they are ready to type, and the layout only moves once, on their action.
  //
  // Resetting `probed` HERE, keyed on `nextId`, is what makes "at most once per
  // question" true across a resume as well as a normal advance: `nextId` is the
  // server's cursor, so a reload mid-Q2 comes back with the latch cleared and
  // the owner gets their one re-ask, rather than a flag that outlived its
  // question or one restored from a tab that is gone.
  useEffect(() => {
    setText("");
    setProbed(false);
    setNotice(null);
    if (nextId && !mobile) taRef.current?.focus();
  }, [nextId, mobile]);

  // `noAnswer` is the Q3 "Nothing to add" path. It still travels the wire as
  // `skipped: true`, and the server still drops it from `answers_for_prompt` —
  // which is right, and is not a leftover of the old skip: "nothing to add"
  // means there IS no exclusion to feed the prompt. The wire flag is unchanged;
  // only what the screen offers, and on which question, has changed.
  const send = useCallback(async (noAnswer) => {
    if (!nextId || busy) return;          // double-submit / double-tap guard
    const answer = text.trim();

    if (!noAnswer) {
      // Empty box. Send is `disabled` here, so this arrives from Enter or from
      // the wrapper below — either way we say the one line that states these are
      // needed, and we do NOT submit. Nothing typed is not an answer.
      if (!answer) { setNotice("empty"); return; }

      // One word. Re-ask once, and only if the catalog gave this question a
      // probe — no generic fallback, for the same reason there is no generic
      // skip label. Their text is untouched and `probed` latches, so the very
      // next Send submits exactly what is in the box, one word or not.
      if (!probed && !/\s/.test(answer) && INTERVIEW_QUESTIONS[nextId]?.probe) {
        setProbed(true);
        setNotice("probe");
        return;
      }
    }

    setNotice(null);
    setBusy(true);
    try {
      // `skipped` is passed as an EXPLICIT flag, never as an empty answer — the
      // backend refuses to guess "nothing to add" apart from "nothing typed yet".
      await onSubmit(nextId, { answer: noAnswer ? null : answer.slice(0, maxChars), skipped: noAnswer });
    } catch (_) {
      // Already surfaced by the caller's toast. Swallowed here so it does not
      // become an unhandled rejection, and so the box keeps their text: the
      // question is unchanged, so `nextId` is unchanged, so nothing is cleared
      // and pressing Send again just overwrites the same question id.
    } finally {
      setBusy(false);
    }
  }, [nextId, busy, text, maxChars, onSubmit, probed]);

  if (!interview) return null;

  const over = text.length > maxChars;
  // Send is genuinely disabled on an empty box — that disabled state IS the
  // mandatory-ness, and it is not decorative. But a disabled button dispatches
  // no click, so a tap on it would be a dead tap with no explanation. Split the
  // two: `emptyBlocked` drives both the wrapper that catches that tap and the
  // pointer-events opt-out that lets the tap reach the wrapper at all.
  const emptyBlocked = !busy && !text.trim();
  const probeLine = notice === "probe" ? INTERVIEW_QUESTIONS[nextId]?.probe : null;

  return (
    <div data-testid="onboarding-interview" data-interview-state={resolved ? "resolved" : "pending"}>
      <Assistant>
        <p>{pickSourceAware(pick, gen, INTERVIEW_COPY.leadIn, INTERVIEW_COPY.leadInDump, mobile)}</p>
      </Assistant>

      {/* Transcript, rebuilt from the SERVER's answers so a resume shows the
          same history the owner left behind. The one record that can arrive
          `skipped` is now Q3's "Nothing to add"; it leaves the question on
          screen and nothing else — no bubble, no ack, no "no problem". There is
          nothing to echo back and nothing to reassure about. The `!rec.skipped`
          branch is kept rather than narrowed to Q3, because it also covers rows
          written by an older build, which are still in the database. */}
      {order.map((qid) => {
        const rec = answers[qid];
        const prompt = catalog?.[qid];
        if (!rec) return null;
        if (!prompt) return null;
        return (
          <React.Fragment key={qid}>
            <Assistant><p>{prompt}</p></Assistant>
            {!rec.skipped && rec.text ? (
              <>
                <UserReply>{rec.text}</UserReply>
                <Assistant>
                  <p data-testid="onboarding-interview-ack">{INTERVIEW_COPY.ack.d}</p>
                </Assistant>
              </>
            ) : null}
          </React.Fragment>
        );
      })}

      {/* The live question */}
      {nextId && catalog?.[nextId] && (
        <>
          <Assistant>
            <p
              id="interview-question"
              className="font-medium"
              data-testid="onboarding-interview-question"
              data-question-id={nextId}
            >
              {catalog[nextId]}
            </p>
            {INTERVIEW_QUESTIONS[nextId]?.hint && (
              <p id="interview-hint" className="text-lm-text3 text-sm mt-1">
                {pick(INTERVIEW_QUESTIONS[nextId].hint, mobile)}
              </p>
            )}
          </Assistant>
          <QuickReplies>
            <div className="rounded-2xl border border-lm-border bg-lm-surface p-3 shadow-sm">
              {/* Named BY the question bubble rather than by an sr-only label
                  repeating it: with a label AND the live region below, the same
                  sentence was being read three times per question. */}
              <textarea
                id="interview-answer"
                aria-labelledby="interview-question"
                aria-describedby={INTERVIEW_QUESTIONS[nextId]?.hint ? "interview-hint" : undefined}
                ref={taRef}
                data-testid="onboarding-interview-input"
                value={text}
                onChange={(e) => {
                  setText(e.target.value.slice(0, maxChars));
                  // Typing answers the empty line, so retire it the moment they
                  // do. The "probe" line is NOT cleared here: it is the re-ask
                  // they are currently responding to, and yanking it away
                  // mid-sentence takes `probeAccept` — the permission to send
                  // what they have — off screen with it.
                  setNotice((n) => (n === "empty" ? null : n));
                }}
                onKeyDown={(e) => {
                  // Enter sends on desktop, newline on mobile (where Enter is
                  // the only way to get a line break at all).
                  if (e.key === "Enter" && !e.shiftKey && !mobile) {
                    e.preventDefault();
                    send(false);
                  }
                }}
                rows={mobile ? 3 : 2}
                maxLength={maxChars}
                disabled={busy}
                placeholder={pick(
                  INTERVIEW_QUESTIONS[nextId]?.placeholder || { d: "" }, mobile
                )}
                className="w-full resize-none bg-transparent text-lm-text placeholder:text-lm-text3/70 text-sm leading-relaxed outline-none px-1 py-1"
              />
              <div className="flex items-center justify-between gap-3 mt-2">
                {/* Counter appears only as the cap gets close — a character
                    count from character one reads as a length requirement. */}
                <span
                  className={`text-xs tabular-nums ${over ? "text-lm-secondary" : "text-lm-text3"}`}
                  data-testid="onboarding-interview-count"
                >
                  {text.length >= maxChars - 100 ? `${text.length} / ${maxChars}` : ""}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Rendered ONLY where the catalog defines `noAnswer` — Q3
                      today. No fallback label: an unrecognised question id gets
                      no second button, because a generic "Skip" is precisely how
                      a skip button comes back on Q1/Q2. Same size and hit area
                      as Send; secondary in colour, not in weight class. It says
                      "Nothing to add", which is an answer, so it is not styled
                      as an exit and is never delayed or put on hover. */}
                  {INTERVIEW_QUESTIONS[nextId]?.noAnswer && (
                    <button
                      type="button"
                      onClick={() => send(true)}
                      disabled={busy}
                      data-testid="onboarding-interview-skip"
                      className="inline-flex items-center justify-center rounded-lg border border-lm-border bg-lm-surface px-5 py-2.5 min-h-[44px] text-sm font-semibold text-lm-text2 hover:bg-lm-tint hover:text-lm-text transition-colors disabled:opacity-60"
                    >
                      {pick(INTERVIEW_QUESTIONS[nextId].noAnswer, mobile)}
                    </button>
                  )}
                  {/* The wrapper exists for one reason: to catch the tap that a
                      disabled Send swallows, so an empty box explains itself
                      instead of doing nothing. Enter covers the same case on
                      desktop via the textarea. */}
                  <span
                    className="inline-flex"
                    onClick={() => { if (emptyBlocked) setNotice("empty"); }}
                  >
                    <button
                      type="button"
                      onClick={() => send(false)}
                      disabled={busy || !text.trim()}
                      data-testid="onboarding-interview-send"
                      className={`inline-flex items-center justify-center rounded-lg bg-lm-primary px-5 py-2.5 min-h-[44px] text-sm font-semibold text-white hover:bg-lm-primaryHover transition-colors disabled:opacity-50 ${emptyBlocked ? "pointer-events-none" : ""}`}
                    >
                      {INTERVIEW_COPY.send.d}
                    </button>
                  </span>
                </div>
              </div>

              {/* The probe pair and the empty line share one slot. The element
                  is always mounted so the live region exists BEFORE its text
                  changes — a conditionally-rendered aria-live region is announced
                  unreliably, which for a message that only ever appears once is
                  the same as not announcing it.

                  Deliberately NOT an error: no red, no `text-lm-secondary` (that
                  is the over-limit colour, four lines up), no icon, no border.
                  Both lines are ordinary sentences in ordinary text colour,
                  because a short answer is a person being brief and an empty box
                  is a person who has not typed yet. Neither is a mistake.

                  The re-ask and the release valve render TOGETHER, in the same
                  colour and the same paragraph — the way out is visible at the
                  same instant as the ask, so the probe can never read as a gate. */}
              <p
                aria-live="polite"
                data-testid="onboarding-interview-probe"
                data-probe={notice || ""}
                className="text-lm-text2 text-sm leading-relaxed px-1 empty:hidden mt-2"
              >
                {notice === "empty" ? pick(INTERVIEW_COPY.probeEmpty, mobile) : null}
                {probeLine ? `${pick(probeLine, mobile)} ${pick(INTERVIEW_COPY.probeAccept, mobile)}` : null}
              </p>
            </div>
          </QuickReplies>
        </>
      )}

      {/* Closing line. Identical whether they wrote a paragraph each or one
          terse line and "Nothing to add" — it branches on how far GENERATION
          got, never on how much they typed. That identity IS the design: a
          closing line that rewards effort is a closing line that punishes
          brevity, and they feel the branch. */}
      {resolved && (closing === "early" || closing === "done") && (
        <Assistant>
          <p data-testid="onboarding-interview-done" data-variant={closing}>
            {closing === "early"
              ? pickSourceAware(pick, gen, INTERVIEW_COPY.doneEarly, INTERVIEW_COPY.doneEarlyDump, mobile)
              : pick(INTERVIEW_COPY.done, mobile)}
          </p>
        </Assistant>
      )}

      {/* Progress, underneath, for the whole interview. */}
      {showProgress && (
        <div className={`pl-11 ${reduced ? "" : "animate-[fadeIn_0.3s_ease]"}`}>
          <GenWaitStrip gen={gen} mobile={mobile} />
        </div>
      )}

      {/* One tight live region for the whole step, so a screen reader hears the
          new question (and the ack) without us moving focus for them. */}
      <p className="sr-only" role="status" aria-live="polite">
        {nextId && catalog?.[nextId] ? catalog[nextId] : ""}
      </p>
    </div>
  );
}
